import { Link } from '@tanstack/react-router';
import { Check, X } from 'lucide-react';
import { useState } from 'react';
import type { ProposalCard as Proposal } from '@/lib/api/types';
import { msg, t } from '@/lib/i18n';
import { pathTo } from '@/lib/search';
import { Button, Callout, KeyValue, Pill } from '@/ui';
import { useDecideProposal } from './api';
import s from './Assistant.module.css';

const RISK = {
  low: { tone: 'neutral', label: msg('Low risk') },
  medium: { tone: 'medium', label: msg('Medium risk') },
  high: { tone: 'bad', label: msg('High risk') },
} as const;
const STATUS = {
  pending: { tone: 'info', label: msg('Pending') },
  applied: { tone: 'good', label: msg('Applied') },
  rejected: { tone: 'neutral', label: msg('Dismissed') },
  failed: { tone: 'bad', label: msg('Failed') },
} as const;

function show(v: unknown): string {
  if (v == null) return '—';
  if (typeof v === 'string') return v.length > 80 ? `${v.slice(0, 77)}…` : v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  const json = JSON.stringify(v);
  return json.length > 80 ? `${json.slice(0, 77)}…` : json;
}

function humanKey(k: string): string {
  const s = k.replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** What the card shows: an assistant ProposalCard, or a module's own pending proposal (coaching
 * lists them from its workspace) — the same proposal row, applied through the same endpoints. */
export type ProposalView = Pick<Proposal, 'id' | 'label' | 'title' | 'rationale' | 'input'> & {
  risk: string;
  status: string;
  result?: Proposal['result'];
};

/** The model proposes; a person applies (RULES §AI-3). Apply runs the same action a button would. */
export function ProposalCard({ proposal: initial, compact }: { proposal: ProposalView; compact?: boolean }) {
  const [proposal, setProposal] = useState<ProposalView>(initial);
  const decide = useDecideProposal();
  const pending = proposal.status === 'pending';
  const risk = RISK[proposal.risk as keyof typeof RISK];
  const status = STATUS[proposal.status as keyof typeof STATUS];
  const error = decide.error instanceof Error ? decide.error.message : null;
  const result = proposal.result;
  const fields = Object.entries(proposal.input ?? {}).filter(([k, v]) => v !== null && v !== '' && !(Array.isArray(v) && !v.length) && k !== 'reason');

  const run = (decision: 'apply' | 'reject') => decide.mutate({ id: proposal.id, decision }, { onSuccess: (card) => setProposal((p) => ({ ...p, ...card })) });

  return (
    <div className={s.proposal}>
      <div className={s.proposalHead}>
        <span className={s.proposalLabel}>{proposal.label}</span>
        {risk && <Pill tone={risk.tone}>{t(risk.label)}</Pill>}
        {!pending && status && <Pill tone={status.tone}>{t(status.label)}</Pill>}
      </div>
      <p className={s.proposalTitle}>{proposal.title}</p>
      {proposal.rationale && <p className={s.proposalWhy}>{proposal.rationale}</p>}
      {!compact && fields.length > 0 && <KeyValue dense items={fields.slice(0, 8).map(([k, v]) => ({ label: humanKey(k), value: show(v) }))} />}
      {result?.message && <p className={s.proposalResult}>{result.message}</p>}
      {result?.error && <Callout tone="danger">{result.error}</Callout>}
      {result?.links && result.links.length > 0 && (
        <div className={s.links}>
          {result.links.map((l) => (
            <Link key={l.href} {...pathTo(l.href)} className={s.linkChip}>
              {l.label}
            </Link>
          ))}
        </div>
      )}
      {error && <Callout tone="danger">{error}</Callout>}
      {pending && (
        <div className={s.proposalActions}>
          <Button variant="primary" size="sm" icon={Check} loading={decide.isPending && decide.variables?.decision === 'apply'} onClick={() => run('apply')}>
            {t('Apply')}
          </Button>
          <Button variant="ghost" size="sm" icon={X} disabled={decide.isPending} onClick={() => run('reject')}>
            {t('Dismiss')}
          </Button>
        </div>
      )}
    </div>
  );
}
